import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Alert, Space, Typography, Select, Popconfirm, Tag, Grid, theme } from 'antd';
import { NumberOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import ResponsiveTable from '../../../components/Common/ResponsiveTable';

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const FolioSeriesTab = ({ 
  companies,
  folioSeries,
  selectedCompany,
  loading,
  onCompanySelect,
  onNew,
  onEdit,
  onDelete,
  onToggleStatus,
  onSetAsDefault
,
  readOnly = false
}) => {
  const { token } = theme.useToken();
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = useRef(null);
  const screens = useBreakpoint();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, [selectedCompany]);

  const folioSerieColumns = [
    {
      title: 'Serie',
      dataIndex: 'serie',
      key: 'serie',
    },
    {
      title: 'Folio Actual',
      dataIndex: 'folio',
      key: 'folio',
    },
    {
      title: 'Estado',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? 'Activa' : 'Inactiva'}
        </Tag>
      ),
    },
    {
      title: 'Predeterminada',
      dataIndex: 'is_default',
      key: 'is_default',
      render: (isDefault) => (
        isDefault ? <Tag color="blue">Predeterminada</Tag> : null
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          >
            Editar
          </Button>
          {!record.is_default && (
            <Button
              type="link"
              onClick={() => onSetAsDefault(record.id)}
            >
              Establecer como predeterminada
            </Button>
          )}
          <Button
            type="link"
            icon={record.enabled ? <CloseOutlined /> : <CheckOutlined />}
            onClick={() => onToggleStatus(record.id)}
          >
            {record.enabled ? 'Desactivar' : 'Activar'}
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta serie?"
            description="Esta acción no se puede deshacer."
            onConfirm={() => onDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(col => !(readOnly && col.key === 'actions'))

  return (
    <Card>
      <div style={{ marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
          Gestión de Series de Folio
        </Title>
        
        <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ marginBottom: '16px', width: screens.xs ? '100%' : 'auto' }}>
          <Select
            placeholder="Selecciona una empresa"
            style={{ width: screens.xs ? '100%' : 400 }}
            value={selectedCompany?.id}
            onChange={onCompanySelect}
          >
            {companies.map(company => (
              <Option key={company.id} value={company.id}>
                {company.tax_name} - {company.tax_id}
              </Option>
            ))}
          </Select>
          
          {!readOnly && <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!selectedCompany}
            onClick={onNew}
            block={screens.xs}
          >
            Nueva Serie
          </Button>}
        </Space>
      </div>

      <Alert
        message="Información"
        description="Las series de folio se utilizan para generar números consecutivos en las facturas. Cada empresa puede tener múltiples series, pero solo una puede estar marcada como predeterminada."
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      {selectedCompany && (
        <>
          <div ref={topPaginationRef} />
          <ResponsiveTable
            columns={folioSerieColumns}
            dataSource={folioSeries}
            rowKey="id"
            loading={loading}
            locale={{ emptyText: 'No hay series de folio registradas para esta empresa' }}
            pagination={{
              position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}

      {!selectedCompany && (
        <div style={{ textAlign: 'center', padding: '40px', color: token.colorTextTertiary }}>
          Selecciona una empresa para ver sus series de folio
        </div>
      )}
    </Card>
  );
};

export default FolioSeriesTab;